import React, { createContext, useState, useEffect } from 'react';
import authService from '../services/authService';

// Create context
export const AuthContext = createContext();

/**
 * Auth Context Provider
 * Manages authentication state and provides auth methods
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is already logged in on mount
  useEffect(() => {
    const storedToken = authService.getToken();

    const hydrateSession = async () => {
      if (storedToken && authService.isTokenValid()) {
        try {
          setToken(storedToken);
          const userInfo = await authService.getCurrentUser();
          setUser(userInfo);
          setIsAuthenticated(true);
        } catch (err) {
          authService.logout();
          setToken(null);
          setUser(null);
          setIsAuthenticated(false);
        }
      }

      setIsLoading(false);
    };

    hydrateSession();
  }, []);

  /**
   * Login user with username and password
   */
  const login = async (username, password) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await authService.login(username, password);
      
      // Fetch user info
      const userInfo = await authService.getCurrentUser();
      
      setToken(response.access_token);
      setUser(userInfo);
      setIsAuthenticated(true);
      
      return { success: true, user: userInfo };
    } catch (err) {
      const errorMessage = err?.detail || err?.message || 'Login failed';
      setError(errorMessage);
      setIsAuthenticated(false);
      
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Logout user
   */
  const logout = () => {
    setIsLoading(true);
    
    try {
      authService.logout();
      setToken(null);
      setUser(null);
      setIsAuthenticated(false);
      setError(null);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Clear error
   */
  const clearError = () => {
    setError(null);
  };

  const value = {
    user,
    token,
    isAuthenticated,
    isLoading,
    error,
    login,
    logout,
    clearError,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
